import React, { useState } from "react";
import styled from "styled-components";
import { Table as BsTable } from "react-bootstrap";
import IconContainer from "../../organisms/IconContainer";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import {
  AsistenciaFuturaSpa,
  AsistenciaPasadaSpa,
  AsistenciaProyectadaSpa,
  IAsistenciaProyectadaSpa,
  QUERY_OBTENER_LISTAS_ASISTENCIAS_PROYECTADAS_SPA,
  esAsistenciaPasada,
} from "../../../services/asistenciaSpaServicios";
import { PrettyHorarioSpa } from "../../../services/agendamientosSpaServicios";
import ReagendamientoSpaModal from "../../reagendamiento/ReagendamientoSpaModal";
import { useQueryClient } from "@tanstack/react-query";
import { stringADate } from "../../../utils/dateUtils";

function Table({
  items,
  fecha,
}: {
  items: AsistenciaProyectadaSpa[];
  fecha: string;
}) {
  const queryClient = useQueryClient();

  const [itemAReprogramar, setItemAReprogramar] = useState<
    IAsistenciaProyectadaSpa | undefined
  >(undefined);

  const primero = items[0];

  const onCloseReagendamientoModal = () => {
    setItemAReprogramar(undefined);
    setTimeout(() => {
      queryClient.invalidateQueries([
        QUERY_OBTENER_LISTAS_ASISTENCIAS_PROYECTADAS_SPA,
      ]);
    }, 1000);
  };

  const onReagendarClick = (item: IAsistenciaProyectadaSpa) => {
    setItemAReprogramar(item);
  };

  return (
    <>
      {!!itemAReprogramar && (
        <ReagendamientoSpaModal
          idReserva={itemAReprogramar.idReservaSpa}
          itemAsistencia={itemAReprogramar}
          fecha={stringADate(fecha)}
          onClose={onCloseReagendamientoModal}
        />
      )}
      {esAsistenciaPasada(primero) ? (
        <TablePasada
          items={items as AsistenciaPasadaSpa[]}
          onReagendarClick={onReagendarClick}
        />
      ) : (
        <TableFutura
          items={items as AsistenciaFuturaSpa[]}
          onReagendarClick={onReagendarClick}
        />
      )}
    </>
  );
}

function TablePasada({
  items,
  onReagendarClick,
}: {
  items: AsistenciaPasadaSpa[];
  onReagendarClick: (item: IAsistenciaProyectadaSpa) => void;
}) {
  return (
    <StyledTable responsive hover>
      <thead>
        <tr>
          <StyledColHeader>No.</StyledColHeader>
          <StyledColHeader>Mascota</StyledColHeader>
          <StyledColHeader>Propietario</StyledColHeader>
          <StyledColHeader>Horario</StyledColHeader>
          <StyledColHeader>Ingresó</StyledColHeader>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {items.map((_, i) => (
          <TableItemPasada
            key={_.idMascota}
            item={_}
            no={i + 1}
            onReagendarClick={onReagendarClick}
          />
        ))}
      </tbody>
    </StyledTable>
  );
}

function TableItemPasada({
  no,
  item,
  onReagendarClick,
}: {
  no: number;
  item: AsistenciaPasadaSpa;
  onReagendarClick: (item: IAsistenciaProyectadaSpa) => void;
}) {
  return (
    <DynamicRow isSuccess={item.asistio}>
      <StyledCell>{no}</StyledCell>
      <StyledCell>{item.nombreMascota}</StyledCell>
      <StyledCell>{item.nombrePropietario}</StyledCell>
      <StyledCell>{PrettyHorarioSpa[item.horario]}</StyledCell>
      <StyledCell>
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            checked={item.asistio}
            id="flexCheckDefault"
            disabled
          />
        </div>
      </StyledCell>
      <StyledCell>
        {!item.asistio && (
          <IconContainer
            onClick={() => {
              onReagendarClick(item);
            }}
            style={{ cursor: "pointer" }}
          >
            <EventRepeatIcon />
          </IconContainer>
        )}
      </StyledCell>
    </DynamicRow>
  );
}

function TableFutura({
  items,
  onReagendarClick,
}: {
  items: AsistenciaFuturaSpa[];
  onReagendarClick: (item: IAsistenciaProyectadaSpa) => void;
}) {
  return (
    <StyledTable responsive hover>
      <thead>
        <tr>
          <StyledColHeader>No.</StyledColHeader>
          <StyledColHeader>Mascota</StyledColHeader>
          <StyledColHeader>Propietario</StyledColHeader>
          <StyledColHeader>Horario</StyledColHeader>
        </tr>
      </thead>
      <tbody>
        {items.map((_, i) => (
          <TableItemFutura
            key={_.idMascota}
            item={_}
            no={i + 1}
            onReagendarClick={onReagendarClick}
          />
        ))}
      </tbody>
    </StyledTable>
  );
}

function TableItemFutura({
  no,
  item,
  onReagendarClick,
}: {
  no: number;
  item: AsistenciaFuturaSpa;
  onReagendarClick: (item: IAsistenciaProyectadaSpa) => void;
}) {
  return (
    <DynamicRow isSuccess={false}>
      <StyledCell>{no}</StyledCell>
      <StyledCell>{item.nombreMascota}</StyledCell>
      <StyledCell>{item.nombrePropietario}</StyledCell>
      <StyledCell>{PrettyHorarioSpa[item.horario]}</StyledCell>
      <StyledCell>
        <IconContainer
          onClick={() => {
            onReagendarClick(item);
          }}
          style={{ cursor: "pointer" }}
        >
          <EventRepeatIcon />
        </IconContainer>
      </StyledCell>
    </DynamicRow>
  );
}

function DynamicRow({
  children,
  isSuccess,
}: {
  children: React.ReactNode;
  isSuccess: boolean;
}) {
  if (isSuccess) {
    return <StyledSuccessRow>{children}</StyledSuccessRow>;
  } else {
    return <StyledRow>{children}</StyledRow>;
  }
}

const StyledTable = styled(BsTable)``;

const StyledCell = styled("td")``;

const StyledColHeader = styled("th")`
  color: ${(props) => props.theme.colors.main};
`;

const StyledRow = styled("tr")``;

const StyledSuccessRow = styled(StyledRow)`
  background-color: ${(props) => props.theme.colors.successShadow};
`;

export default Table;
